import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { CheckCircle2, CalendarDays, Truck, MapPin, PackageCheck, ArrowLeft, Phone, Clock } from 'lucide-react';
type OrderStep = 'confirmed' | 'scheduled' | 'dispatched' | 'en_route' | 'delivered';
const STEPS = [{
  id: 'confirmed',
  label: 'Order Confirmed',
  icon: CheckCircle2,
  desc: 'We received your order.'
}, {
  id: 'scheduled',
  label: 'Scheduled',
  icon: CalendarDays,
  desc: 'Delivery window confirmed.'
}, {
  id: 'dispatched',
  label: 'Dispatched',
  icon: ClipboardCheck,
  desc: 'Driver assigned and loading.'
}, {
  id: 'en_route',
  label: 'En Route',
  icon: Truck,
  desc: 'Truck is on the way to your site.'
}, {
  id: 'delivered',
  label: 'Delivered',
  icon: PackageCheck,
  desc: 'Material successfully dumped.'
}];
// Need to import ClipboardCheck locally since it's not in the main import list above
import { ClipboardCheck } from 'lucide-react';
export function TrackOrder() {
  const {
    orderId
  } = useParams<{
    orderId: string;
  }>();
  const [orderData, setOrderData] = useState<any>(null);
  const [currentStepIndex, setCurrentStepIndex] = useState(1); // Start at 'scheduled'
  useEffect(() => {
    window.scrollTo(0, 0);
    const saved = localStorage.getItem('lastOrder');
    if (saved) {
      try {
        const parsed = JSON.parse(saved);
        if (parsed.orderId === orderId) {
          setOrderData(parsed);
        }
      } catch (e) {
        console.error('Could not parse order data');
      }
    }
    // Simulate progression for demo purposes
    const timer1 = setTimeout(() => setCurrentStepIndex(2), 6000); // Dispatched
    const timer2 = setTimeout(() => setCurrentStepIndex(3), 12000); // En Route
    const timer3 = setTimeout(() => setCurrentStepIndex(4), 18000); // Delivered
    return () => {
      clearTimeout(timer1);
      clearTimeout(timer2);
      clearTimeout(timer3);
    };
  }, [orderId]);
  if (!orderData) {
    return <div className="min-h-[60vh] flex flex-col items-center justify-center text-center px-4 bg-[#fdfbf7] dark:bg-[#1b1b1b]" data-id="element-2602">
        <h1 className="text-2xl font-bold text-brand-charcoal dark:text-white mb-4" data-id="element-2603">
          Order Not Found
        </h1>
        <p className="text-stone-500 mb-6" data-id="element-2604">
          We couldn't find tracking details for order {orderId}.
        </p>
        <Link to="/" className="text-brand-orange hover:underline font-medium" data-id="element-2605">
          Return to Home
        </Link>
      </div>;
  }
  return <main className="py-12 md:py-20 bg-[#fdfbf7] dark:bg-[#1b1b1b] min-h-[calc(100vh-64px)]" data-id="element-2606">
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-2607">
        <Link to="/" className="inline-flex items-center text-sm font-medium text-brand-muted hover:text-brand-orange transition-colors mb-8" data-id="element-2608">
          <ArrowLeft className="w-4 h-4 mr-2" data-id="element-2609" />
          Back to Home
        </Link>

        <div className="flex flex-col md:flex-row items-start md:items-center justify-between mb-8 gap-4" data-id="element-2610">
          <div data-id="element-2611">
            <h1 className="font-heading text-3xl font-bold text-brand-charcoal dark:text-white" data-id="element-2612">
              Track Order
            </h1>
            <p className="text-stone-500 dark:text-stone-400 mt-1" data-id="element-2613">
              Order #{orderData.orderId}
            </p>
          </div>
          {currentStepIndex < 4 && <div className="inline-flex items-center px-3 py-1.5 rounded-full bg-brand-orange/10 border border-brand-orange/20 text-brand-orange text-sm font-bold" data-id="element-2614">
              <span className="w-2 h-2 rounded-full bg-brand-orange animate-pulse mr-2" data-id="element-2615" />
              You're #2 in the queue
            </div>}
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-8" data-id="element-2616">
          {/* Left Col: Map & Summary */}
          <div className="md:col-span-2 space-y-6" data-id="element-2617">
            {/* Mock Map */}
            <div className="bg-white dark:bg-[#222] rounded-2xl border border-stone-200 dark:border-[#333] overflow-hidden shadow-sm h-64 relative" data-id="element-2618">
              <div className="absolute inset-0 bg-gradient-to-br from-stone-100 to-stone-200 dark:from-[#2a2a2a] dark:to-[#1a1a1a]" data-id="element-2619" />
              {/* Fake route line */}
              <svg className="absolute inset-0 w-full h-full opacity-30 dark:opacity-20" preserveAspectRatio="none" viewBox="0 0 100 100" data-id="element-2620">
                <path d="M10,90 Q30,50 50,50 T90,10" fill="none" stroke="#1DB954" strokeWidth="2" strokeDasharray="4 4" data-id="element-2621" />
              </svg>
              {/* Fake pins */}
              <div className="absolute bottom-4 left-4 w-4 h-4 bg-brand-charcoal dark:bg-white rounded-full border-2 border-white dark:border-black shadow-md" data-id="element-2622" />
              <div className="absolute top-4 right-4 w-4 h-4 bg-brand-orange rounded-full border-2 border-white dark:border-black shadow-md" data-id="element-2623" />

              {/* Animated Truck */}
              {currentStepIndex >= 2 && currentStepIndex < 4 && <motion.div className="absolute z-10 bg-white dark:bg-black p-2 rounded-full shadow-lg border border-stone-200 dark:border-[#444]" initial={{
              left: '10%',
              top: '80%'
            }} animate={{
              left: currentStepIndex === 3 ? '50%' : '20%',
              top: currentStepIndex === 3 ? '50%' : '70%'
            }} transition={{
              duration: 2,
              ease: 'easeInOut'
            }} data-id="element-2624">
                  <Truck className="w-5 h-5 text-[#1DB954]" data-id="element-2625" />
                </motion.div>}

              <div className="absolute top-4 left-4 bg-white/90 dark:bg-black/80 backdrop-blur-sm px-4 py-2 rounded-lg border border-stone-200 dark:border-[#444] shadow-sm" data-id="element-2626">
                <p className="text-xs font-bold text-stone-500 dark:text-stone-400 uppercase tracking-wider mb-0.5" data-id="element-2627">
                  Live ETA
                </p>
                <p className="text-lg font-bold text-brand-charcoal dark:text-white" data-id="element-2628">
                  {currentStepIndex === 4 ? 'Delivered' : '45 mins'}
                </p>
              </div>
            </div>

            {/* Order Summary */}
            <div className="bg-white dark:bg-[#222] rounded-2xl border border-stone-200 dark:border-[#333] p-6 shadow-sm" data-id="element-2629">
              <h3 className="font-bold text-brand-charcoal dark:text-white mb-4 flex items-center" data-id="element-2630">
                <FileText className="w-5 h-5 mr-2 text-brand-orange" data-id="element-2631" />
                Order Details
              </h3>

              <div className="space-y-4" data-id="element-2632">
                <div className="flex items-start gap-3" data-id="element-2633">
                  <MapPin className="w-5 h-5 text-stone-400 shrink-0 mt-0.5" data-id="element-2634" />
                  <div data-id="element-2635">
                    <p className="text-sm font-medium text-stone-900 dark:text-white" data-id="element-2636">
                      {orderData.address}
                    </p>
                    <p className="text-xs text-stone-500 mt-0.5" data-id="element-2637">
                      Instructions: {orderData.instructions || 'None'}
                    </p>
                  </div>
                </div>

                <div className="flex items-start gap-3" data-id="element-2638">
                  <Clock className="w-5 h-5 text-stone-400 shrink-0 mt-0.5" data-id="element-2639" />
                  <div data-id="element-2640">
                    <p className="text-sm font-medium text-stone-900 dark:text-white" data-id="element-2641">
                      {new Date(orderData.date).toLocaleDateString('en-US', {
                      weekday: 'long',
                      month: 'short',
                      day: 'numeric'
                    })}
                    </p>
                    <p className="text-xs text-stone-500 mt-0.5" data-id="element-2642">
                      Window: {orderData.time || 'Any time'}
                    </p>
                  </div>
                </div>

                <div className="border-t border-stone-100 dark:border-[#333] pt-4 mt-4" data-id="element-2643">
                  {orderData.items.map((item: any, i: number) => <div key={i} className="flex justify-between text-sm mb-2" data-id="element-2644">
                      <span className="text-stone-700 dark:text-stone-300" data-id="element-2645">
                        {item.quantity} yds {item.product}
                      </span>
                      <span className="font-medium text-stone-900 dark:text-white" data-id="element-2646">
                        ${(item.quantity * item.pricePerYard).toFixed(2)}
                      </span>
                    </div>)}
                  <div className="flex justify-between text-sm mb-2 text-stone-500" data-id="element-2647">
                    <span data-id="element-2648">Delivery Fee</span>
                    <span data-id="element-2649">${orderData.totalDeliveryFee.toFixed(2)}</span>
                  </div>
                  <div className="flex justify-between text-sm mb-2 text-stone-500" data-id="element-2650">
                    <span data-id="element-2651">Tax</span>
                    <span data-id="element-2652">${orderData.estimatedTax.toFixed(2)}</span>
                  </div>
                  <div className="flex justify-between font-bold text-lg mt-4 pt-4 border-t border-stone-100 dark:border-[#333] text-brand-charcoal dark:text-white" data-id="element-2653">
                    <span data-id="element-2654">Total</span>
                    <span data-id="element-2655">${orderData.finalTotal.toFixed(2)}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Right Col: Timeline */}
          <div className="bg-white dark:bg-[#222] rounded-2xl border border-stone-200 dark:border-[#333] p-6 shadow-sm h-fit" data-id="element-2656">
            <h3 className="font-bold text-brand-charcoal dark:text-white mb-6" data-id="element-2657">
              Status
            </h3>

            <div className="relative" data-id="element-2658">
              {/* Vertical line */}
              <div className="absolute left-4 top-4 bottom-4 w-0.5 bg-stone-100 dark:bg-[#333]" data-id="element-2659" />

              <div className="space-y-8 relative" data-id="element-2660">
                {STEPS.map((step, index) => {
                const isPast = index < currentStepIndex;
                const isCurrent = index === currentStepIndex;
                const isFuture = index > currentStepIndex;
                const Icon = step.icon;
                return <div key={step.id} className="flex items-start gap-4" data-id="element-2661">
                      <div className="relative z-10 bg-white dark:bg-[#222] py-1" data-id="element-2662">
                        <div className={`w-8 h-8 rounded-full flex items-center justify-center border-2 transition-colors duration-500 ${isPast ? 'bg-[#1DB954] border-[#1DB954] text-white' : isCurrent ? 'bg-white dark:bg-[#222] border-[#1DB954] text-[#1DB954]' : 'bg-white dark:bg-[#222] border-stone-200 dark:border-[#444] text-stone-300 dark:text-[#555]'}`} data-id="element-2663">
                          <Icon className="w-4 h-4" data-id="element-2664" />
                        </div>
                        {isCurrent && <span className="absolute inset-0 rounded-full border-2 border-[#1DB954] animate-ping opacity-20" style={{
                      top: '4px',
                      bottom: '4px'
                    }} data-id="element-2665" />}
                      </div>

                      <div className="pt-1.5" data-id="element-2666">
                        <p className={`text-sm font-bold transition-colors duration-500 ${isPast || isCurrent ? 'text-stone-900 dark:text-white' : 'text-stone-400 dark:text-[#666]'}`} data-id="element-2667">
                          {step.label}
                        </p>
                        <p className={`text-xs mt-0.5 transition-colors duration-500 ${isPast || isCurrent ? 'text-stone-500 dark:text-stone-400' : 'text-stone-300 dark:text-[#555]'}`} data-id="element-2668">
                          {step.desc}
                        </p>
                        {isPast && <p className="text-[10px] text-stone-400 mt-1" data-id="element-2669">
                            {new Date(Date.now() - (4 - index) * 600000).toLocaleTimeString([], {
                        hour: '2-digit',
                        minute: '2-digit'
                      })}
                          </p>}
                      </div>
                    </div>;
              })}
              </div>
            </div>

            <div className="mt-8 pt-6 border-t border-stone-100 dark:border-[#333]" data-id="element-2670">
              <p className="text-xs text-stone-500 mb-3" data-id="element-2671">
                Need help with this order?
              </p>
              <a href="tel:206-619-3742" className="flex items-center justify-center w-full py-2.5 rounded-lg border border-stone-200 dark:border-[#444] text-sm font-medium text-stone-700 dark:text-stone-300 hover:bg-stone-50 dark:hover:bg-[#2a2a2a] transition-colors" data-id="element-2672">
                <Phone className="w-4 h-4 mr-2" data-id="element-2673" />
                Call Dispatch
              </a>
            </div>
          </div>
        </div>
      </div>
    </main>;
}